import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Select,
  MenuItem,
  InputAdornment,
  TextField,
  Box,
} from '@mui/material';
import CustomTypography from './CustomTypography';
import CustomFormControlForSelect from './CustomFormControlForSelect';
import {
  MobileStateQuestion,
  SaveDataAnswerGroup,
  MobileStateAnswer,
  ScriptToolTipTipType,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import { debounce } from '../../utils';

interface SearchableSingleSelectProps {
  question: MobileStateQuestion;
  answers: SaveDataAnswerGroup | undefined;
  disabled?: boolean;
  stringMapper: (key: string) => string;
  onSetAnswer: (questionKey: string, answers: string[]) => void;
  showTitle?: boolean;
}

const SearchIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='11' cy='11' r='8' />
    <line x1='21' y1='21' x2='16.65' y2='16.65' />
  </svg>
);

const SearchableDropDownSelector = ({
  question,
  answers,
  disabled,
  stringMapper,
  onSetAnswer,
  showTitle,
}: SearchableSingleSelectProps) => {
  const options = useMemo(() => {
    const defaultOption: MobileStateAnswer = {
      TextKey: 'Select Option',
      OverrideText: 'Select Option',
      Sort: 0,
      Terminal: 'Y',
      ToolTip: {
        message: 'Select an Option',
        type: 'info' as ScriptToolTipTipType,
      },
    };
    const answers = question?.Answers || [];
    const alreadyHasDefault = answers.some(
      (a) => a.TextKey === defaultOption.TextKey
    );
    return question?.Required == false && !alreadyHasDefault
      ? [defaultOption, ...answers]
      : [...answers];
  }, [question?.Answers, question?.Required]);

  const [searchTerm, setSearchTerm] = useState('');
  const [userAnswer, setUserAnswer] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuOpenTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Populate user answer from answers prop
  useEffect(() => {
    const newAnswers: string[] = [];
    if (answers !== undefined) {
      for (const item of answers.Answers) {
        if (item?.Active) {
          newAnswers.push(item?.Answer);
        }
      }
    }
    setUserAnswer(newAnswers[0] || '');
  }, [answers]);

  // Focus the search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the dropdown is fully rendered
      menuOpenTimeoutRef.current = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    } else {
      setSearchTerm('');
      if (menuOpenTimeoutRef.current) {
        clearTimeout(menuOpenTimeoutRef.current);
      }
    }

    // Cleanup the timeout on unmount or when isOpen changes
    return () => {
      if (menuOpenTimeoutRef.current) {
        clearTimeout(menuOpenTimeoutRef.current);
      }
    };
  }, [isOpen]);

  // Filter options based on search input
  const filteredOptions = useMemo(() => {
    return options.filter((option) => {
      const optionText =
        option?.OverrideText ??
        (stringMapper ? stringMapper(option?.TextKey) : option?.TextKey);
      /// const optionText = stringMapper
      //? stringMapper(option.TextKey)
      //: option.TextKey;
      return optionText.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [searchTerm, options, stringMapper]);

  const selectedText = useMemo(() => {
    return options.filter((option) => {
      const optionText =
        option?.OverrideText ??
        (stringMapper ? stringMapper(option?.TextKey) : option?.TextKey);
      /// const optionText = stringMapper
      //? stringMapper(option.TextKey)
      //: option.TextKey;
      return optionText.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [options, searchTerm, stringMapper]);

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setSearchTerm(event.target.value);
  };

  // Debounced function to set answer
  const setAnswer = debounce((answer: string) => {
    if (onSetAnswer) {
      onSetAnswer(question?.QuestionKey, [answer]);
    }
  }, 100);

  const handleSelectChange = (event: any) => {
    if (disabled) return;
    const answer = event.target.value;
    setUserAnswer(answer);
    setAnswer(answer);
    setIsOpen(false);
  };

  // Handle clicks inside the search field
  const handleSearchClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    // Ensure search input stays focused when clicked
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle open state for the dropdown
  const handleOpen = () => {
    setIsOpen(true);
  };

  // Handle close state for the dropdown
  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <CustomFormControlForSelect label={''} error={false} helperText={''}>
      {showTitle && (
        <CustomTypography
          variant='h5'
          sx={{
            color: '#343330',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: 'normal',
            marginBottom: '0.5rem',
          }}
        >
          {stringMapper(question?.Title)}
        </CustomTypography>
      )}
      <Select
        disabled={disabled}
        value={userAnswer}
        onChange={handleSelectChange}
        fullWidth
        displayEmpty
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        renderValue={(selected) => {
          if (!selected) {
            return <em>Select Value</em>;
          }
          //if (selectedOption === undefined) {  return ''; }
          const selectedOption = options.find(
            (option) => option.TextKey === selected
          );
          const optionText =
            selectedOption?.OverrideText ??
            (stringMapper ? stringMapper(selected) : selected);
          return optionText;
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 250,
              overflowY: 'auto',
              // Remove the marginTop here that was causing the dropdown to move
            },
            // Add elevation for visual separation without moving the dropdown
            elevation: 3,
          },
          // Position directly below the select without any offset
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          // These settings will ensure the menu stays anchored correctly
          slotProps: {
            paper: {
              elevation: 3,
              // no margins or spacing to cause movement
            },
          },
          disablePortal: true, // Important: keep dropdown in DOM flow
          disableRestoreFocus: true,
          autoFocus: false,
          // Remove any margin styling from sx that would cause movement
        }}
        sx={{
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            minHeight: '40px',
          },
          // Ensure the select component itself has consistent styling
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
          },
          // Remove any transitions that might cause movement
          '& .MuiSelect-icon': {
            transition: 'none',
          },
        }}
      >
        {/* Search Bar Inside the Dropdown */}
        <MenuItem
          style={{
            padding: '8px',
            position: 'sticky', // Make search bar sticky at top
            top: 0,
            backgroundColor: 'white',
            zIndex: 1, // Ensure it stays on top of scrolling items
          }}
          disableRipple
          onClick={handleSearchClick}
        >
          <TextField
            inputRef={inputRef}
            size='small'
            fullWidth
            placeholder='Search...'
            value={searchTerm}
            onChange={handleSearchChange}
            onClick={handleSearchClick}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            autoFocus
          />
        </MenuItem>

        {/* Filtered Options */}
        {filteredOptions.map((option, index) => (
          <MenuItem key={index} value={option?.TextKey}>
            {option?.OverrideText ??
              (stringMapper ? stringMapper(option?.TextKey) : option?.TextKey)}
          </MenuItem>
        ))}

        {/* No Options Found */}
        {filteredOptions.length === 0 && (
          <MenuItem disabled>No options found</MenuItem>
        )}
      </Select>
    </CustomFormControlForSelect>
  );
};

export default SearchableDropDownSelector;
